import React from "react";
import { Spinner, Container, Row, Col } from "react-bootstrap";

const LoadingSpinner = ({
	size = "md",
	variant = "primary",
	centered = false,
	overlay = false,
	message = "Loading...",
	fullPage = false,
	className = "",
}) => {
	const sizeMap = {
		sm: { width: "1rem", height: "1rem" },
		md: { width: "2rem", height: "2rem" },
		lg: { width: "3rem", height: "3rem" },
		xl: { width: "4rem", height: "4rem" },
	};

	const spinnerStyle = sizeMap[size] || sizeMap.md;

	const SpinnerElement = (
		<div className={`loading-spinner ${className}`} style={{ flexDirection: "column", alignItems: "center" }}>
			<div className="d-flex justify-content-center">
				<Spinner
					animation="border"
					variant={variant}
					style={spinnerStyle}
					role="status"
					aria-label="Loading"
				/>
			</div>
			{message && (
				<div className="loading-message text-muted d-flex justify-content-center pt-2 fw-bold">
					{message}
				</div>
			)}
		</div>
	);

	// Full page loading overlay
	if (fullPage) {
		return (
			<div
				className="loading-overlay-fullpage"
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(255, 255, 255, 0.9)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					zIndex: 9999,
					flexDirection: "column",
				}}>
				{SpinnerElement}
			</div>
		);
	}

	// Overlay loading (relative to parent)
	if (overlay) {
		return (
			<div
				className="loading-overlay"
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(255, 255, 255, 0.8)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					zIndex: 100,
					flexDirection: "column",
				}}>
				{SpinnerElement}
			</div>
		);
	}

	// Centered loading
	if (centered) {
		return (
			<Container>
				<Row className="justify-content-center">
					<Col xs="auto" className="text-center">
						{SpinnerElement}
					</Col>
				</Row>
			</Container>
		);
	}

	// Inline loading
	return SpinnerElement;
};

// Specialized loading components
export const TableLoadingSpinner = ({ message = "Loading data..." }) => (
	<LoadingSpinner
		size="md"
		centered
		message={message}
		className="table-loading"
	/>
);

export const ButtonLoadingSpinner = ({
	size = "sm",
	message = null,
	position = "left", // 'left' | 'right'
	variant = "light",
	className = "",
}) => {
	// We'll render inline so we can control order of spinner and message
	const sizeMap = {
		sm: { width: "1rem", height: "1rem" },
		md: { width: "2rem", height: "2rem" },
		lg: { width: "3rem", height: "3rem" },
		xl: { width: "4rem", height: "4rem" },
	};

	const spinnerStyle = sizeMap[size] || sizeMap.sm;

	const spinner = (
		<Spinner
			animation="border"
			variant={variant}
			style={spinnerStyle}
			role="status"
			aria-label="Loading"
			className={position === "left" ? "me-2" : "ms-2"}
		/>
	);

	return (
		<span className={`d-inline-flex align-items-center button-loading ${className}`}>
			{position === "left" && spinner}
			{message && <span className="fw-bold">{message}</span>}
			{position === "right" && spinner}
		</span>
	);
};

export const PageLoadingSpinner = ({ message = "Loading page..." }) => (
	<LoadingSpinner
		size="lg"
		fullPage
		message={message}
		className="page-loading"
	/>
);

export const ModalLoadingSpinner = ({ message = "Processing..." }) => (
	<LoadingSpinner
		size="md"
		overlay
		message={message}
		className="modal-loading"
	/>
);

export default LoadingSpinner;
